<script setup lang="ts">
import { ScalarIconButton } from '@scalar/components/icon-button'
import { ScalarIconEye, ScalarIconEyeSlash, ScalarIconX } from '@scalar/icons'
import type { XScalarEnvironment } from '@scalar/workspace-store/schemas/extensions/document/x-scalar-environments'
import { computed, ref, useTemplateRef } from 'vue'

import type { VueClassProp } from '@/types/vue'
import { CodeInputLite } from '@/v2/components/code-input'
import { useLocalization } from '@/v2/features/localization'

import DataTableCell from './DataTableCell.vue'
import DataTableInputSelect from './DataTableInputSelect.vue'

const props = withDefaults(
  defineProps<{
    id?: string
    type?: string | undefined
    /** Class for the wrapping cell because attrs is bound to the input */
    containerClass?: VueClassProp | undefined
    required?: boolean
    modelValue: string
    /** Allows adding a custom value to the enum dropdown, defaults to true */
    canAddCustomEnumValue?: boolean
    readOnly?: boolean
    enum?: string[]
    environment: XScalarEnvironment
    description?: string | undefined
    /** Whether to show context function suggestions like $guid, $timestamp. Defaults to true */
    withFakeData?: boolean
  }>(),
  {
    canAddCustomEnumValue: true,
    required: false,
    readOnly: false,
    withFakeData: true,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', v: string | number): void
  (e: 'inputFocus'): void
  (e: 'inputBlur'): void
  (e: 'selectVariable', value: string): void
}>()

const { translate } = useLocalization()

defineOptions({ inheritAttrs: false })

const mask = ref(true)
const interactingWithDropdown = ref(false)
const codeInput = useTemplateRef('codeInput')

const handleBlur = () => !interactingWithDropdown.value && emit('inputBlur')

const inputType = computed(() =>
  props.type === 'password' ? 'text' : (props.type ?? 'text'),
)

// If not an enum nor read only, focus the code input
const handleLabelClick = () => {
  if (!props.enum?.length && !props.readOnly) {
    codeInput.value?.focus()
  }
}
</script>
<template>
  <DataTableCell
    class="relative flex"
    :class="containerClass">
    <div
      v-if="$slots.default"
      class="text-c-1 flex items-center pr-0 pl-3"
      :for="id ?? ''"
      @click="handleLabelClick">
      <slot />:
    </div>
    <div class="relative flex min-w-0 flex-1">
      <template v-if="props.enum && props.enum.length">
        <DataTableInputSelect
          :canAddCustomValue="props.canAddCustomEnumValue"
          :modelValue="props.modelValue"
          :value="props.enum"
          @update:modelValue="emit('update:modelValue', $event)" />
      </template>
      <template v-else>
        <input
          v-if="mask && type === 'password'"
          v-bind="id ? { ...$attrs, id: id } : $attrs"
          autocomplete="off"
          class="text-c-1 disabled:text-c-2 peer w-full min-w-0 border-none px-2 py-1.25 -outline-offset-1"
          :class="{ 'scalar-password-input': type === 'password' }"
          data-1p-ignore
          :readOnly="readOnly"
          spellcheck="false"
          :type="inputType"
          :value="modelValue"
          @input="
            emit(
              'update:modelValue',
              ($event.target as HTMLInputElement).value ?? '',
            )
          " />
        <CodeInputLite
          v-else
          v-bind="$attrs"
          :id="id"
          ref="codeInput"
          class="text-c-1 disabled:text-c-2 peer w-full min-w-0 border-none -outline-offset-1"
          :class="[
            type === 'password' && description && 'pr-12',
            description && 'pr-8',
            type === 'password' && 'scalar-password-input',
          ]"
          :environment="environment"
          :modelValue="modelValue ?? ''"
          :readOnly="readOnly"
          :required="Boolean(required)"
          :type="inputType"
          :withFakeData="withFakeData"
          @blur="handleBlur"
          @focus="emit('inputFocus')"
          @update:modelValue="(value) => emit('update:modelValue', value)" />
      </template>
    </div>
    <div
      v-if="$slots.warning"
      class="centered-y text-orange absolute right-7 text-xs">
      <slot name="warning" />
    </div>
    <slot name="icon" />
    <!-- Clear -->
    <ScalarIconButton
      v-if="modelValue"
      class="-ml-.25 h-6 w-6 self-center p-1.25"
      :icon="ScalarIconX"
      :label="translate('apiClient.dataTableInput.clearValue')"
      @click="emit('update:modelValue', '')" />
    <!-- Toggle Visibility -->
    <ScalarIconButton
      v-if="type === 'password'"
      class="-ml-.5 mr-1.25 h-6 w-6 self-center p-1.25"
      data-testid="data-table-password-toggle"
      :icon="mask ? ScalarIconEye : ScalarIconEyeSlash"
      :label="
        mask
          ? translate('apiClient.dataTableInput.showPassword')
          : translate('apiClient.dataTableInput.hidePassword')
      "
      @click="mask = !mask" />
  </DataTableCell>
</template>

<style scoped>
:deep(.code-input-lite__editor) {
  background-color: transparent;
  font-family: var(--scalar-font);
  font-size: var(--scalar-small);
  padding: 5px 8px;
}
:deep(.scalar-pill:not(:last-of-type)) {
  margin-right: 0.15em;
}
:deep(.scalar-pill:not(:first-of-type)) {
  margin-left: 0.5px;
}
/* Tailwind placeholder is busted */
input::placeholder {
  color: var(--scalar-color-3);
}
/* we want our inputs to look like a password input but not be one */
.scalar-password-input {
  text-security: disc;
  -webkit-text-security: disc;
  -moz-text-security: disc;
}
</style>
